/**
 * Schedule-X calendar helpers (Temporal + event mapping).
 *
 * Schedule-X validates dates with `instanceof Temporal.PlainDate`. The library may use
 * bare global `Temporal` or a bundled import; values passed into our API are bridged
 * via ISO strings at compile-time boundaries (`temporal-spec` vs `temporal-polyfill`).
 *
 * Runtime uses one constructor (`globalThis.Temporal` in `src/polyfills/index.ts`, with
 * `resolve.dedupe` and the schedule-x temporal plugins in `vite.config.ts`).
 */

import { InstantInterval } from '@airreading/common';
import type { CalendarEventExternal } from '@schedule-x/calendar';
import { SessionStatus } from '@airreading/common/data';
import { Temporal } from 'temporal-polyfill';

import type { ConcreteEvent, ExpandedEvent } from './session-schedule-calendar-types.js';

type ScheduleXCalendarEvent = CalendarEventExternal & {
  timeRangeFormatted?: string;
  durationMinutes?: number;
  status?: SessionStatus;
  coachSessionRemarksV2?: ConcreteEvent['coachSessionRemarksV2'];
};

const NOON_PLAIN_TIME = '12:00';

/** Scroll offset matches legacy RBC `scrollToTime` (−15 minutes). */
const SCROLL_OFFSET_MINUTES = 15;

type GlobalWithTemporal = typeof globalThis & { Temporal?: typeof Temporal };

/** Schedule-X callbacks use temporal-spec types; bridge via ISO strings at boundaries. */
type TemporalStringLike = { toString(): string };

/** Polyfill realm shared with Schedule-X (`globalThis.Temporal` or module import). */
function scheduleXTemporal(): typeof Temporal {
  return (globalThis as GlobalWithTemporal).Temporal ?? Temporal;
}

/** Schedule-X callback PlainDate → app `temporal-polyfill` PlainDate. */
export function fromScheduleXPlainDate(date: TemporalStringLike): Temporal.PlainDate {
  return Temporal.PlainDate.from(date.toString());
}

/** Schedule-X callback Instant → app `temporal-polyfill` Instant. */
export function fromScheduleXInstant(instant: TemporalStringLike): Temporal.Instant {
  return Temporal.Instant.from(instant.toString());
}

/** App-side PlainDate re-wrap (alias for clarity at call sites). */
export const toScheduleXPlainDate = fromScheduleXPlainDate;

/**
 * PlainDate for Schedule-X `selectedDate` / `setDate` only.
 * Schedule-X .d.ts uses global `Temporal` (ESNext lib); same constructor at runtime.
 */
export function toScheduleXApiPlainDate(date: TemporalStringLike) {
  return Temporal.PlainDate.from(date.toString()) as never;
}

function toScheduleXZonedDateTime(dateTime: TemporalStringLike) {
  return scheduleXTemporal().ZonedDateTime.from(dateTime.toString());
}

/** Schedule-X `onRangeUpdate` / `getRange` → utils that expect polyfill ZonedDateTime. */
export function scheduleXRangeToPolyfill(range: { start: TemporalStringLike; end: TemporalStringLike }): {
  start: Temporal.ZonedDateTime;
  end: Temporal.ZonedDateTime;
} {
  return {
    start: Temporal.ZonedDateTime.from(range.start.toString()),
    end: Temporal.ZonedDateTime.from(range.end.toString()),
  };
}

/** Schedule-X range → `InstantInterval` for URL / data fetching (matches legacy calendar). */
export function instantIntervalFromScheduleXRange(range: {
  start: TemporalStringLike;
  end: TemporalStringLike;
}): InstantInterval {
  const { start, end } = scheduleXRangeToPolyfill(range);
  return InstantInterval.from(fromScheduleXInstant(start.toInstant()), fromScheduleXInstant(end.toInstant()));
}

/** Calendar event end from Schedule-X → polyfill Instant (for past-session styling). */
export function scheduleXEventEndToInstant(end: CalendarEventExternal['end']): Temporal.Instant | null {
  if (end == null) {
    return null;
  }
  if (typeof end === 'object' && 'toInstant' in end && typeof end.toInstant === 'function') {
    return fromScheduleXInstant(end.toInstant());
  }
  return null;
}

/** Noon on a civil day in the coach timezone (month → day drill-down). */
export function noonInstantOnPlainDate(plainDate: Temporal.PlainDate, timeZone: string): Temporal.Instant {
  return plainDate
    .toZonedDateTime({
      timeZone,
      plainTime: Temporal.PlainTime.from(NOON_PLAIN_TIME),
    })
    .toInstant();
}

/** Parse Schedule-X custom component date strings (e.g. week grid header). */
export function scheduleXPlainDateFromString(isoDate: string): Temporal.PlainDate {
  return Temporal.PlainDate.from(isoDate);
}

/**
 * YYYY-MM-DD from a JS `Date` in Schedule-X month-grid custom components.
 * Uses the Date’s local civil fields (same as the grid cell the library renders).
 */
export function isoDateFromJsDate(jsDate: Date): string {
  const y = jsDate.getFullYear();
  const m = String(jsDate.getMonth() + 1).padStart(2, '0');
  const d = String(jsDate.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** `HH:mm` for Schedule-X scroll-controller `initialScroll` / `scrollTo`. */
export function scheduleXTimeString(instant: Temporal.Instant, timeZone: string): string {
  const zdt = scheduleXTemporal()
    .Instant.from(instant.toString())
    .toZonedDateTimeISO(timeZone)
    .subtract({ minutes: SCROLL_OFFSET_MINUTES });
  const hour = String(zdt.hour).padStart(2, '0');
  const minute = String(zdt.minute).padStart(2, '0');
  return `${hour}:${minute}`;
}

/** Grid cell fallback when Schedule-X renders before React context catches up. */
export function expandedEventFromScheduleX(calendarEvent: CalendarEventExternal): ExpandedEvent | null {
  const start = calendarEvent.start;
  const end = calendarEvent.end;
  if (start == null || end == null || typeof start !== 'object' || typeof end !== 'object') {
    return null;
  }
  if (!('toInstant' in start) || !('toInstant' in end)) {
    return null;
  }

  const extra = calendarEvent as ScheduleXCalendarEvent;
  const startInstant = fromScheduleXInstant(start.toInstant());
  const endInstant = fromScheduleXInstant(end.toInstant());

  return {
    id: String(calendarEvent.id),
    title: calendarEvent.title ?? '',
    start: new Date(startInstant.epochMilliseconds),
    end: new Date(endInstant.epochMilliseconds),
    timeRangeFormatted: extra.timeRangeFormatted ?? '',
    durationMinutes: extra.durationMinutes ?? 0,
    status: extra.status ?? SessionStatus.scheduled,
    coachSessionRemarksV2: extra.coachSessionRemarksV2 ?? [],
  };
}

export function concreteEventToScheduleX(event: ConcreteEvent, timeZone: string): CalendarEventExternal {
  const start = toScheduleXZonedDateTime(event.eventInterval.start.toZonedDateTimeISO(timeZone));
  const end = toScheduleXZonedDateTime(event.eventInterval.end.toZonedDateTimeISO(timeZone));

  return {
    id: event.id,
    title: event.title,
    start: start as CalendarEventExternal['start'],
    end: end as CalendarEventExternal['end'],
    timeRangeFormatted: event.timeRangeFormatted,
    durationMinutes: event.durationMinutes,
    status: event.status,
    coachSessionRemarksV2: event.coachSessionRemarksV2,
    _options: {
      disableDND: true,
      disableResize: true,
    },
  };
}
